import { useEffect, useMemo, useSyncExternalStore } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { useStore } from 'jotai';

import {
  holdRecordingPause,
  releaseRecordingPause,
  resolveRecordingStatus,
  RECORDING_FAILURE_REASONS,
  RECORDING_PAUSE_EFFECTS,
  RECORDING_PAUSE_REASONS,
  SCREEN_RECORDING_STATUSES,
  type RecordingEvent,
  type RecordingFailure,
  type RecordingPauseReason,
  type RecordingPauseTransition,
  type ScreenRecordingPort,
  type ScreenRecordingStartInput,
  type ScreenRecordingStatus,
} from '@/shared-responder-v2-core';
import {
  attachMicToRecordingGraph,
  createPlaybackRoute,
  createRecordingAudioGraph,
  disposePlaybackRoute,
  disposeRecordingAudioGraph,
  requestDisplayStream,
  requestRecordingMicStream,
  watchFrameStall,
  withTimeout,
  type FrameStallWatch,
  type PlaybackRoute,
  type RecordingAudioGraph,
  buildRecordingFileName,
  cloneAudioStream,
  finalizeUploadWithinTimeout,
  isAudioStreamLive,
  pickScreenMimeType,
  queueRecordingMetadata,
  resolveMarkerSeconds,
  resolveRecordingClockSeconds,
  resolveRecordingMicSource,
  resolveUploadProgress,
  shouldIgnoreCaptureEnded,
  toCaptureEndedFailure,
  toInsightRecordingEventType,
  toRecordingFailure,
  toRecordingStatus,
  toRecordingUploadFailure,
  METADATA_WRITE_TIMEOUT_MS,
  RECORDING_MIC_SOURCES,
  RECORDING_UPLOAD_MIME_TYPE,
  type RecordingMetadataPatch,
} from '@/shared-responder-v2-gateway';
import { selectedMicDeviceIdAtom } from '@/shared-responder-v2-shell';
import {
  createResumableUpload,
  supportsDisplayMedia,
} from '@/shared-utilities';
import {
  ADD_RECORDING_EVENT,
  CREATE_SURVEY_SIGNED_UPLOAD_URL,
  SignedUrlAction,
  UPDATE_INSIGHT_RECORDING,
} from '@/survey-graphql';

/** The legacy recorder's timeslice — one resumable chunk every four seconds. */
const RECORDING_CHUNK_INTERVAL_MS = 4000;

/** Ceiling on waiting for the recorder's closing chunk before releasing capture. */
const RECORDER_DRAIN_TIMEOUT_MS = 2000;

const NO_PAUSE_REASONS: ReadonlySet<RecordingPauseReason> = new Set();

const UNSUPPORTED_FAILURE: RecordingFailure = {
  reason: RECORDING_FAILURE_REASONS.Unsupported,
  message: null,
  isRetryable: false,
};

interface RecordingSignedUpload {
  readonly url: string;
  readonly path: string;
}

/**
 * The reactive half of the port, republished as a value on every change.
 *
 * The port itself is a stable object created once, and the survey app builds with
 * `reactCompiler: true`, so every expression derived from `port.status` was
 * memoised on that unchanging identity and never recomputed: capture reached
 * `recording` while the session still read `requesting` and sat behind its
 * permission screen until the fail-open fired. A snapshot changes identity when
 * the status does, which is what no memoisation can swallow.
 */
interface ScreenRecordingSnapshot {
  readonly status: ScreenRecordingStatus;
  readonly uploadProgress: number;
  readonly pauseReasons: ReadonlySet<RecordingPauseReason>;
  readonly failure: RecordingFailure | null;
}

type SharedMicSubscription = (
  listener: (stream: MediaStream) => void
) => () => void;

interface ScreenRecordingDeps {
  readonly readMicDeviceId: () => string | null;
  readonly subscribeSharedMicStream?: SharedMicSubscription;
  readonly requestSignedUpload: (
    fileName: string
  ) => Promise<RecordingSignedUpload | null>;
  readonly writeRecordingMetadata: (
    insightId: string,
    patch: RecordingMetadataPatch
  ) => Promise<void>;
  readonly reportDuration: (
    insightId: string,
    durationSeconds: number,
    isUnloading?: boolean
  ) => Promise<void>;
  readonly reportEvent: (
    insightId: string,
    event: RecordingEvent,
    timeSeconds: number
  ) => Promise<void>;
}

/**
 * A hook-owned recorder rather than the legacy module singleton: two mounted
 * sessions used to share one `MediaRecorder`, and its listener sets were
 * cleared on the first teardown, which left the second deaf. Everything below
 * lives in this closure and dies with it.
 */
function createScreenRecordingPort(deps: ScreenRecordingDeps) {
  let captureStatus: ScreenRecordingStatus = SCREEN_RECORDING_STATUSES.Idle;
  let pauseReasons: ReadonlySet<RecordingPauseReason> = NO_PAUSE_REASONS;
  let failure: RecordingFailure | null = null;
  let insightId: string | null = null;
  let playbackElement: HTMLAudioElement | null = null;
  let playbackRoute: PlaybackRoute | null = null;
  let isDisposed = false;

  let screenStream: MediaStream | null = null;
  let micStream: MediaStream | null = null;
  let isMicStreamOwned = false;
  let unfollowSharedMic: (() => void) | null = null;
  let audioGraph: RecordingAudioGraph | null = null;
  let frameWatch: FrameStallWatch | null = null;
  let recorder: MediaRecorder | null = null;
  let upload: ReturnType<typeof createResumableUpload> | null = null;

  let startedAtMs: number | null = null;
  let pausedTotalMs = 0;
  let pausedSinceMs: number | null = null;
  let pendingMetadata: readonly RecordingMetadataPatch[] = [];

  const listeners = new Set<() => void>();

  function readUploadProgress(): number {
    return upload === null
      ? 0
      : resolveUploadProgress({
          confirmedBytes: upload.getConfirmedBytes(),
          pendingBytes: upload.getPendingBytes(),
        });
  }

  let snapshot: ScreenRecordingSnapshot = {
    status: captureStatus,
    uploadProgress: 0,
    pauseReasons,
    failure,
  };

  /**
   * Rebuilt only here, so `getSnapshot` can hand React the same object until
   * something actually changes — returning a fresh one per call is what makes
   * `useSyncExternalStore` throw.
   */
  function notify(): void {
    snapshot = {
      status: resolveRecordingStatus({ captureStatus, pauseReasons }),
      uploadProgress: readUploadProgress(),
      pauseReasons,
      failure,
    };

    for (const listener of listeners) listener();
  }

  function holdMetadata(patch: RecordingMetadataPatch): void {
    pendingMetadata = queueRecordingMetadata(pendingMetadata, patch);
  }

  /**
   * Drains the held `updateInsightRecording` fields. Called from the points that
   * prove an insight row exists — a turn the backend accepted, and the final
   * stop — because the row is materialised by the responder's first answer and
   * not by the session start. A patch whose mutation fails goes back on the
   * queue so the next accepted turn tries again.
   */
  async function flushMetadata(): Promise<void> {
    const activeInsightId = insightId;
    const draining = pendingMetadata;

    if (activeInsightId === null || draining.length === 0) {
      return;
    }

    pendingMetadata = [];

    for (const patch of draining) {
      try {
        await deps.writeRecordingMetadata(activeInsightId, patch);
      } catch {
        holdMetadata(patch);
      }
    }
  }

  function fail(next: RecordingFailure): void {
    failure = next;
    captureStatus = toRecordingStatus(next);
    notify();
  }

  function clockSeconds(): number {
    return resolveRecordingClockSeconds({
      startedAtMs,
      nowMs: Date.now(),
      pausedTotalMs,
      pausedSinceMs,
    });
  }

  /**
   * A hold names a condition of one capture session — an idle responder, a muted
   * track — so it dies with that session. Carried across, the reason set reads as
   * paused while the next share is genuinely recording, and the question renders
   * its pause overlay over a live recorder.
   */
  function clearPauseHolds(): void {
    pauseReasons = NO_PAUSE_REASONS;
    pausedSinceMs = null;
  }

  function routePlayback(): PlaybackRoute {
    if (playbackRoute?.element !== playbackElement) {
      disposePlaybackRoute(playbackRoute);
      playbackRoute = null;
    }

    playbackRoute ??= createPlaybackRoute(playbackElement);

    return playbackRoute;
  }

  function releaseMic(): void {
    if (isMicStreamOwned) {
      for (const track of micStream?.getTracks() ?? []) track.stop();
    }

    micStream = null;
    isMicStreamOwned = false;
  }

  function mixSharedMic(graph: RecordingAudioGraph, shared: MediaStream): void {
    if (audioGraph !== graph || isAudioStreamLive(micStream)) return;

    let borrowed: MediaStream | null = null;

    try {
      borrowed = cloneAudioStream(shared);

      if (borrowed === null) return;

      releaseMic();
      attachMicToRecordingGraph(graph, borrowed);
    } catch (error) {
      console.error('Screen recording could not mix the microphone', error);

      for (const track of borrowed?.getTracks() ?? []) track.stop();

      return;
    }

    micStream = borrowed;
    isMicStreamOwned = true;
  }

  function releaseCapture(): void {
    unfollowSharedMic?.();
    unfollowSharedMic = null;
    frameWatch?.dispose();
    disposeRecordingAudioGraph(audioGraph);

    for (const track of screenStream?.getTracks() ?? []) track.stop();

    releaseMic();

    frameWatch = null;
    audioGraph = null;
    screenStream = null;
    recorder = null;

    clearPauseHolds();
    notify();
  }

  function applyPauseTransition(transition: RecordingPauseTransition): void {
    pauseReasons = transition.reasons;

    const isPausing =
      transition.effect === RECORDING_PAUSE_EFFECTS.Pause &&
      recorder?.state === 'recording';
    const isResuming =
      transition.effect === RECORDING_PAUSE_EFFECTS.Resume &&
      recorder?.state === 'paused';

    if (isPausing && recorder !== null) {
      recorder.requestData();
      recorder.pause();
      pausedSinceMs = Date.now();
    }

    if (isResuming && recorder !== null) {
      recorder.resume();
      pausedTotalMs += pausedSinceMs === null ? 0 : Date.now() - pausedSinceMs;
      pausedSinceMs = null;
    }

    notify();
  }

  function pause(reason: RecordingPauseReason): void {
    applyPauseTransition(holdRecordingPause(pauseReasons, reason));
  }

  function resume(reason: RecordingPauseReason): void {
    applyPauseTransition(releaseRecordingPause(pauseReasons, reason));
  }

  function onCaptureEnded(): void {
    if (shouldIgnoreCaptureEnded(captureStatus)) return;

    void finalizeActiveUpload().then(({ uploadFailed }) => {
      fail(
        uploadFailed
          ? toRecordingUploadFailure(new Error('upload'))
          : toCaptureEndedFailure()
      );
    });
  }

  function beginRecorder(combined: MediaStream, mimeType: string): void {
    const nextRecorder = new MediaRecorder(combined, { mimeType });

    nextRecorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) void upload?.uploadChunk(event.data);

      // Upload progress now travels in the snapshot, so it advances on the
      // recorder's own timeslice rather than whenever something else re-renders.
      notify();
    });

    nextRecorder.addEventListener('error', () => {
      fail(toRecordingUploadFailure(new Error('recorder')));
    });

    recorder = nextRecorder;
    startedAtMs = Date.now();
    pausedTotalMs = 0;
    pausedSinceMs = null;

    nextRecorder.start(RECORDING_CHUNK_INTERVAL_MS);
    captureStatus = SCREEN_RECORDING_STATUSES.Recording;
    failure = null;
    notify();
  }

  async function start(input: ScreenRecordingStartInput): Promise<void> {
    if (captureStatus === SCREEN_RECORDING_STATUSES.Requesting) return;
    if (captureStatus === SCREEN_RECORDING_STATUSES.Recording) return;

    if (!supportsDisplayMedia() || typeof MediaRecorder === 'undefined') {
      fail(UNSUPPORTED_FAILURE);
      return;
    }

    const mimeType = pickScreenMimeType((candidate) =>
      MediaRecorder.isTypeSupported(candidate)
    );

    if (mimeType === null) {
      fail(UNSUPPORTED_FAILURE);
      return;
    }

    insightId = input.insightId;
    captureStatus = SCREEN_RECORDING_STATUSES.Requesting;
    failure = null;
    releaseCapture();

    try {
      screenStream = await requestDisplayStream();
    } catch (error) {
      releaseCapture();
      fail(toRecordingFailure(error));

      return;
    }

    captureStatus = SCREEN_RECORDING_STATUSES.Starting;
    notify();

    /*
     * Everything past the grant settles a status or throws. It must not throw
     * silently: `start` is called as `void start(...)`, so an escaping rejection
     * — `requestSignedUpload` is a bare mutation, and `MediaRecorder` and the
     * audio graph both throw synchronously — left the port on `requesting`
     * for the rest of the session, with the screen genuinely shared and the
     * session waiting on a prompt that had already been answered.
     */
    try {
      /*
       * A spoken session's microphone opens only after this grant, so it joins
       * later as a clone the recording owns: one prompt, and like the legacy
       * recorder it stays in the soundtrack when the responder switches to text.
       * A relayed session has no local microphone: the responder speaks into the phone.
       */
      const micSource = resolveRecordingMicSource(input);
      const ownsSessionMic = micSource === RECORDING_MIC_SOURCES.Own;

      micStream = ownsSessionMic
        ? await requestRecordingMicStream(deps.readMicDeviceId())
        : null;
      isMicStreamOwned = ownsSessionMic;

      if (isDisposed) {
        releaseCapture();

        return;
      }

      audioGraph = createRecordingAudioGraph({
        micStream,
        route: routePlayback(),
      });

      if (micSource === RECORDING_MIC_SOURCES.Shared) {
        const graph = audioGraph;

        unfollowSharedMic =
          deps.subscribeSharedMicStream?.((shared) =>
            mixSharedMic(graph, shared)
          ) ?? null;
      }

      const [videoTrack] = screenStream.getVideoTracks();

      videoTrack?.addEventListener('ended', onCaptureEnded);
      videoTrack?.addEventListener('mute', () =>
        pause(RECORDING_PAUSE_REASONS.ScreenShareMuted)
      );
      videoTrack?.addEventListener('unmute', () =>
        resume(RECORDING_PAUSE_REASONS.ScreenShareMuted)
      );

      frameWatch = watchFrameStall(screenStream, (isStalled) => {
        if (isStalled) pause(RECORDING_PAUSE_REASONS.ScreenShareMuted);
        else resume(RECORDING_PAUSE_REASONS.ScreenShareMuted);
      });

      const signed = await deps.requestSignedUpload(
        buildRecordingFileName(input.insightId)
      );

      if (isDisposed) return;

      if (signed === null) {
        releaseCapture();
        fail(toRecordingUploadFailure(new Error('signed url')));

        return;
      }

      /*
       * Sent as soon as the URL comes back rather than queued for the terminal
       * flush: a closed tab or an early unmount never reaches `stop()`, and
       * `flushOnUnload` only ever reports duration. Queuing this the way
       * `sessionUri` below is queued would lose the whole recording on either
       * path. A failed send still falls back to the queue, so `markEvent` and
       * `stop()` keep retrying it exactly as before.
       */
      void deps
        .writeRecordingMetadata(input.insightId, {
          type: 'gcsKey',
          value: signed.path,
        })
        .catch(() => holdMetadata({ type: 'gcsKey', value: signed.path }));

      upload = createResumableUpload(signed.url);

      void upload
        .getSessionUriPromise()
        .then((sessionUri) =>
          holdMetadata({ type: 'sessionUri', value: sessionUri })
        )
        .catch(() => undefined);

      beginRecorder(
        new MediaStream([
          ...screenStream.getVideoTracks(),
          ...(audioGraph?.destination.stream.getAudioTracks() ?? []),
        ]),
        mimeType
      );
    } catch (error) {
      console.error('Screen recording failed to start', error);
      releaseCapture();
      fail(toRecordingUploadFailure(error));
    }
  }

  /**
   * `MediaRecorder` emits its last chunk after `stop()`, not before it, so
   * tearing the streams down synchronously drops however much of the recording
   * had not yet reached a timeslice boundary — up to four seconds of the end of
   * the test. The `stop` event lands after that final `dataavailable`, which is
   * why it is what the drain waits on.
   */
  function drainRecorder(): Promise<void> {
    const active = recorder;

    if (active === null || active.state === 'inactive') {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      const timeout = setTimeout(resolve, RECORDER_DRAIN_TIMEOUT_MS);

      active.addEventListener(
        'stop',
        () => {
          clearTimeout(timeout);
          resolve();
        },
        { once: true }
      );

      active.stop();
    });
  }

  async function finalizeActiveUpload(): Promise<{ uploadFailed: boolean }> {
    const activeUpload = upload;
    const activeInsightId = insightId;
    const duration = clockSeconds();

    if (activeUpload !== null) {
      captureStatus = SCREEN_RECORDING_STATUSES.Finalizing;
      notify();
    }

    await drainRecorder();
    releaseCapture();

    if (activeUpload === null) {
      return { uploadFailed: false };
    }

    activeUpload.setRecordingStopped();

    const finalizedInTime = await finalizeUploadWithinTimeout(activeUpload);

    await withTimeout(flushMetadata(), METADATA_WRITE_TIMEOUT_MS, undefined);

    if (activeInsightId !== null && duration > 0) {
      await withTimeout(
        deps.reportDuration(activeInsightId, duration),
        METADATA_WRITE_TIMEOUT_MS,
        undefined
      );
    }

    upload = null;
    startedAtMs = null;

    return { uploadFailed: !finalizedInTime || activeUpload.hasError() };
  }

  async function stop(): Promise<void> {
    const { uploadFailed } = await finalizeActiveUpload();

    captureStatus = uploadFailed
      ? SCREEN_RECORDING_STATUSES.Failed
      : SCREEN_RECORDING_STATUSES.Idle;
    failure = uploadFailed
      ? toRecordingUploadFailure(new Error('upload'))
      : null;
    notify();
  }

  /**
   * The first marker follows the first accepted turn, so it is also the first
   * moment the insight is known to exist — the held recording metadata goes out
   * ahead of it rather than waiting for the session to end.
   */
  function markEvent(event: RecordingEvent): void {
    if (insightId === null || startedAtMs === null) return;

    const activeInsightId = insightId;
    const timeSeconds = resolveMarkerSeconds({
      stampedSeconds: event.atSeconds,
      nowSeconds: clockSeconds(),
    });

    void flushMetadata()
      .then(() => deps.reportEvent(activeInsightId, event, timeSeconds))
      .catch(() => undefined);
  }

  function elapsedSeconds(): number | null {
    if (insightId === null || startedAtMs === null) return null;

    return clockSeconds();
  }

  /**
   * A tab closed mid-test never reaches `stop`, so the recording would keep
   * whatever duration the last confirmed chunk implied. Both calls here go out
   * as `keepalive` requests, which is what lets them outlive the document —
   * the legacy recorder used `sendBeacon` against a hardcoded relative path
   * that only resolved behind a dev proxy.
   */
  function flushOnUnload(): void {
    if (insightId === null || startedAtMs === null) return;

    upload?.finalizeSync();
    void deps
      .reportDuration(insightId, clockSeconds(), true)
      .catch(() => undefined);
  }

  return {
    actions: { start, stop, pause, resume, markEvent, elapsedSeconds },
    getSnapshot: (): ScreenRecordingSnapshot => snapshot,
    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener);

      return () => listeners.delete(listener);
    },
    flushOnUnload,
    dispose(): void {
      isDisposed = true;
      releaseCapture();
      disposePlaybackRoute(playbackRoute);
      playbackRoute = null;
    },
    setPlaybackElement(element: HTMLAudioElement | null): void {
      playbackElement = element;
    },
  };
}

export function useSurveyScreenRecordingPort(
  playbackElement: HTMLAudioElement | null,
  subscribeSharedMicStream?: SharedMicSubscription
): ScreenRecordingPort {
  const client = useApolloClient();

  const store = useStore();

  const runtime = useMemo(
    () =>
      createScreenRecordingPort({
        readMicDeviceId: () => store.get(selectedMicDeviceIdAtom),
        subscribeSharedMicStream,
        requestSignedUpload: async (fileName) => {
          const { data } = await client.mutate({
            mutation: CREATE_SURVEY_SIGNED_UPLOAD_URL,
            variables: {
              input: {
                fileName,
                mimeType: RECORDING_UPLOAD_MIME_TYPE,
                isPublic: false,
                action: SignedUrlAction.Resumable,
              },
            },
          });
          const signed = data?.createSurveySignedUploadUrl ?? null;

          return signed === null
            ? null
            : { url: signed.url, path: signed.path };
        },
        writeRecordingMetadata: async (insightId, patch) => {
          await client.mutate({
            mutation: UPDATE_INSIGHT_RECORDING,
            variables: {
              input:
                patch.type === 'gcsKey'
                  ? { insightId, gcsKey: patch.value }
                  : { insightId, sessionUri: patch.value },
            },
          });
        },
        reportDuration: async (insightId, durationSeconds, isUnloading) => {
          await client.mutate({
            mutation: UPDATE_INSIGHT_RECORDING,
            variables: {
              input: { insightId, duration: Math.floor(durationSeconds) },
            },
            context:
              isUnloading === true
                ? { fetchOptions: { keepalive: true } }
                : undefined,
          });
        },
        reportEvent: async (insightId, event, timeSeconds) => {
          await client.mutate({
            mutation: ADD_RECORDING_EVENT,
            variables: {
              input: {
                insightId,
                messageId: event.messageId,
                time: timeSeconds,
                type: toInsightRecordingEventType(event.type),
              },
            },
          });
        },
      }),
    [client, store, subscribeSharedMicStream]
  );

  const snapshot = useSyncExternalStore(
    runtime.subscribe,
    runtime.getSnapshot,
    runtime.getSnapshot
  );

  useEffect(() => {
    runtime.setPlaybackElement(playbackElement);
  }, [playbackElement, runtime]);

  useEffect(() => {
    window.addEventListener('beforeunload', runtime.flushOnUnload);

    return () => {
      window.removeEventListener('beforeunload', runtime.flushOnUnload);
      runtime.dispose();
    };
  }, [runtime]);

  return { ...snapshot, ...runtime.actions };
}
